import { useEffect, useRef, useState } from 'react';
import { BFF_WS_BASE } from '../lib/runtimeEnv.js';
import { getActiveClinicRef } from '../lib/clinicSignal.js';
import { getToken } from '../lib/auth.js';

/**
 * `/ws/events` — a nudge when something in this clinic's day changed elsewhere.
 *
 * پذیرش is a shared screen: another workstation confirms a request, a patient
 * withdraws one, the lazy expiry sweep rejects one on somebody else's read. The
 * badge on this desk has to move without anybody pressing refresh.
 *
 * The socket carries a NUDGE, never data: the event's name, the clinic, and the
 * appointment's id, source, status, time, practitioner and `confirmed_by` — the
 * clinic BFF's NUDGE_FIELDS (src/realtime/eventHub.js), built there so no patient
 * field can cross (PM-0382). Everything the screen shows still comes from its
 * GraphQL query, behind its own permission, so a lost frame costs a few seconds of
 * staleness and never a wrong number. That is also why this hook never replaces
 * the screens' polling: it accelerates it.
 *
 * The token and the clinic go in the query string because a WebSocket cannot send
 * headers; the BFF validates the clinic against membership exactly as it
 * validates the `x-active-clinic-id` header (§12, E34).
 *
 * @param types  routing keys to react to, e.g. ['appointment.requested']
 * @param onEvent called with the event; keep it cheap — usually a refetch
 */
export function useClinicEvents(types, onEvent, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  // The handler is read through a ref so a fresh closure every render does not
  // tear the socket down and build another one.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const typesRef = useRef(types);
  typesRef.current = types;

  useEffect(() => {
    if (!enabled) return undefined;
    const token = getToken();
    if (!token) return undefined;

    let socket = null;
    let closed = false;
    let retry = null;
    let backoff = 2000;

    const open = () => {
      if (closed) return;
      const clinic = getActiveClinicRef() || '';
      const url = `${BFF_WS_BASE}/ws/events?token=${encodeURIComponent(token)}`
        + `&clinic=${encodeURIComponent(clinic)}`;
      try {
        socket = new WebSocket(url);
      } catch {
        // A URL the browser refuses outright is not worth a retry loop.
        return;
      }
      socket.onopen = () => { backoff = 2000; setConnected(true); };
      socket.onmessage = (evt) => {
        let payload;
        try { payload = JSON.parse(evt.data); } catch { return; }
        const type = payload?.type;
        if (!type || type === 'connected') return;
        const wanted = typesRef.current;
        if (wanted && wanted.length && !wanted.includes(type)) return;
        handlerRef.current?.(payload);
      };
      socket.onclose = (e) => {
        setConnected(false);
        socket = null;
        // 4003 is «this person has no clinic here» — a refresh cannot fix it, so
        // reconnecting in a loop would only hammer the BFF with the same answer.
        if (closed || e?.code === 4003 || e?.code === 4001) return;
        retry = setTimeout(open, backoff);
        backoff = Math.min(backoff * 2, 30000);
      };
      socket.onerror = () => { /* 'close' follows and handles the retry */ };
    };

    open();
    return () => {
      closed = true;
      clearTimeout(retry);
      try { socket?.close(); } catch { /* already gone */ }
    };
  }, [enabled]);

  return { connected };
}

export default useClinicEvents;
